import Toggles from '@domain/toggles/Toggles'
import { variationType } from '../catalog/catalog'
import Table, { LightVariationsInterface } from '../data/Table'
import Base from './Base'
import Cluster from './Cluster'
import { baseAttribute, baseAttributes, catalogAttribute, DomainModelData } from './composition'
import { Light, VibiaLightParams } from './Light'
import { isModelComposingPendants } from '../utils/utils'

export class Pendant extends Light {
	type = 'Pendant'
	static className = 'Pendant'

	constructor(vibiaLightData: VibiaLightParams, parent?: Base, attributes?: baseAttributes, data?: DomainModelData) {
		super(vibiaLightData, parent, attributes, data)

		if (!this.getAttribute('pendantHeight')) {
			const pendantHeight = this.getDefaultAttributeValue('pendantHeight')
			if (pendantHeight) {
				this.setAttribute('pendantHeight', pendantHeight)
			}
		}

		if (Toggles.isEnabled("logDomainConstructor")) {
			console.log(`%c[Pendant]`, '🍯;background: khaki; color: #444;padding: 3px; border-radius: 5px;', '🍯')
		}
	}




	getCatalogAttributeKeys() {
		return super.getCatalogAttributeKeys().concat(['pendantHeight'])
	}

	isAttributeRemovable(attribute: baseAttribute) {
		if (attribute == 'pendantHeight') {
			return false
		}
		return super.isAttributeRemovable(attribute)
	}

	setAttribute(attribute: baseAttribute, value: string | number) {
		if (attribute == 'wireLength') { // wireLength attribute is calculated instead of persisted. Persist pendantHeight.
			let clusterHeight = 0
			try {
				const cluster = this.getParent() as Cluster
				clusterHeight = cluster.getCanopySize().height
			} catch { }
			const newPendantHeight = clusterHeight + (value as number) + this.getSize()?.height
			return this.setAttribute('pendantHeight', newPendantHeight)
		}

		return super.setAttribute(attribute, value)
	}

	getAttribute(key: baseAttribute, withModifier?: boolean) {
		if (key == 'wireLength') { // Avoid doing this. use getWireLength() instead
			return this.getWireLength()
		}
		return super.getAttribute(key, withModifier)
	}

	getDefaultAttributeValue(attribute: catalogAttribute) {
		let defaultValue = null

		if (!this.wayOfJson) {
			defaultValue = super.getDefaultAttributeValue(attribute) as number
		} else {
			if (attribute == 'pendantHeight') {
				const pendantHeightOptions = this.getAttributeVariations('pendantHeight')
				if (pendantHeightOptions?.standard)
					defaultValue = pendantHeightOptions.standard
			} else {
				defaultValue = super.getDefaultAttributeValue(attribute)
			}
		}

		if (!defaultValue) return defaultValue
		if (attribute == 'pendantHeight') {
			const parent = this.getParent()
			if (parent)
				defaultValue = parent.requestSetAttribute('pendantHeight', defaultValue)
		}
		return defaultValue as string | number
	}

	getWireLength() {
		const pendantHeight = this.getAttribute('pendantHeight')
		let clusterHeight = 0
		try {
			const cluster = this.getParent() as Cluster
			clusterHeight = cluster.getCanopySize().height
		} catch { }
		const wireLength = pendantHeight - clusterHeight - this.getSize()?.height
		return wireLength
	}

	getAttributeVariations(attribute: variationType) {
		if (attribute == 'pendantHeight') {
			// @ts-ignore
			return this?.props?.attributeOptions?.pendantHeight ?? null
		}

		return super.getAttributeVariations(attribute)
	}

	isNewAttributeValid(attribute: baseAttribute, newValue): boolean {
		if (attribute == 'pendantHeight') {
			const pendantHeight = newValue
			const pendantHeightVariation = this.getAttributeVariations(attribute)
			if (!pendantHeightVariation)
				return super.isNewAttributeValid(attribute, newValue)

			const pendantValid = pendantHeight >= pendantHeightVariation.min && pendantHeight <= pendantHeightVariation.max
			return pendantValid && super.isNewAttributeValid(attribute, newValue)
		}

		return super.isNewAttributeValid(attribute, newValue)
	}

	getPendantHeightOptions() {
		// @ts-ignore
		return this.props.attributeOptions.pendantHeight
	}

	getLightOptions(): LightVariationsInterface {
		if (!this.wayOfJson) {
			return super.getLightOptions()
		} else {
			const lightOptions = super.getLightOptions()
			const isComposing = this.isComposingPendant() && (Toggles.isEnabled('filterControlMforCP'))

			if (isComposing) {
				lightOptions.controls = lightOptions.controls.filter(elem => elem.charAt(0) !== 'M')
				lightOptions.controlsV2.options = lightOptions.controlsV2.options.filter(elem => elem.modifier !== "M")
				lightOptions.controlsV2.extraOptions = lightOptions.controlsV2.extraOptions.filter(elem => elem.modifier !== "M")
			}

			const pendantHeight = this.getPendantHeightOptions()
			return {
				...lightOptions,
				pendantHeight
			}
		}
	}

	getSize(total?) {
		const size = super.getSize(total)
		const pendantHeight = this.getAttribute('pendantHeight') || 0
		const lampHeight = size.height || 0
		const to4Decimals = (num) => parseFloat(num.toFixed(4))
		return {
			...size,
			height: to4Decimals(total ? pendantHeight : lampHeight)
		}
	}

	getOrbitTarget(absolute?): number[] {
		const pos = super.getOrbitTarget(absolute)
		return [pos[0], this.getAttribute('pendantHeight') * -1, pos[2]]
	}

	move(deltaX, deltaY, deltaZ, force?, own?): Base {
		own = (this.getParent() as Cluster).isFreeWire() || own

		let returnedElement: Base = this
		if (force) {
			returnedElement = own
				? super.move(deltaX, deltaY, deltaZ, force)
				: this.getParent().move(deltaX, deltaY, deltaZ, force)
		} else {
			returnedElement = own
				? this.getParentByName('Space').moveElement(this, deltaX, deltaY, deltaZ)
				: this.getParent().moveElement(this, deltaX, deltaY, deltaZ)
		}

		return Toggles.isEnabled("moveDomainMethodReturnsMovedElement") ? returnedElement : this
	}

	fromJSON(json, options?: any, instance?: Base, modelData?: DomainModelData) {
		return Pendant.fromJSON(json, options, instance, modelData)
	}

	static dataFactory(code?, pendantHeight?, color?, width?, height?, depth?) {
		return {
			"element": {
				"type": "Pendant",
				"props": {
					"attributes": {
						"color": color ?? "11 Black",
						"pendantHeight": pendantHeight ?? 2,
						"height": height ?? 0,
						"width": width ?? 0,
						"depth": depth ?? 0
					},
					"code": code ?? '0820'
				},
				"_objectId": "18c84bff-4bf4-40fc-9ef2-fbfe3a5f42ad"
			},
			"options": {
				"position": [width ?? 0, height ?? 0, depth ?? 0]
			}
		}
	}

	isComposingPendant() {
		return isModelComposingPendants(this.getCode())
	}
}

export default Pendant
